import {
  parseOrganizationCapabilityBrowserDetailResponseV1,
  parseOrganizationCapabilityBrowserListResponseV1,
  type OrganizationCapabilityBrowserAssetKindV1,
  type OrganizationCapabilityBrowserItemV1,
  type OrganizationCapabilityBrowserListV1,
  type OrganizationCapabilityBrowserSourceKindV1,
} from "@dosia/shared";

export type OrganizationCapabilityAudienceFilter = "all" | "organization" | "roles" | "users";
export type OrganizationCapabilityHealthFilter = "all" | "ready" | "blocked" | "unknown";
export type OrganizationCapabilityKindFilter = "all" | OrganizationCapabilityBrowserAssetKindV1;
export type OrganizationCapabilitySourceFilter = "all" | OrganizationCapabilityBrowserSourceKindV1 | `plugin:${string}`;

export interface OrganizationCapabilityFilters {
  search: string;
  assetKind: OrganizationCapabilityKindFilter;
  source: OrganizationCapabilitySourceFilter;
  audience: OrganizationCapabilityAudienceFilter;
  health: OrganizationCapabilityHealthFilter;
}

export const DEFAULT_ORGANIZATION_CAPABILITY_FILTERS: OrganizationCapabilityFilters = {
  search: "", assetKind: "all", source: "all", audience: "all", health: "all",
};

export function parseOrganizationCapabilityList(raw: unknown, orgCode: string): OrganizationCapabilityBrowserListV1 {
  if (raw == null) throw new Error("empty organization capability list");
  const data = parseOrganizationCapabilityBrowserListResponseV1(raw).data;
  if (data.organizationId !== orgCode) throw new Error("organization mismatch");
  return data;
}

export function parseOrganizationCapabilityDetail(
  raw: unknown,
  orgCode: string,
  catalogItemId: string,
): OrganizationCapabilityBrowserItemV1 {
  if (raw == null) throw new Error("empty organization capability detail");
  const data = parseOrganizationCapabilityBrowserDetailResponseV1(raw).data;
  if (data.organizationId !== orgCode || data.item.catalogItemId !== catalogItemId) {
    throw new Error("organization capability detail mismatch");
  }
  return data.item;
}

export function organizationCapabilityStats(items: readonly OrganizationCapabilityBrowserItemV1[]) {
  return {
    total: items.length,
    skills: items.filter((item) => item.assetKind === "skill").length,
    agents: items.filter((item) => item.assetKind === "agent").length,
    jobTemplates: items.filter((item) => item.assetKind === "job_template").length,
  };
}

export function organizationCapabilitySourceOptions(items: readonly OrganizationCapabilityBrowserItemV1[]): string[] {
  const values = new Set<string>();
  for (const item of items) {
    values.add(item.source.kind);
    for (const plugin of item.source.pluginNames) values.add(`plugin:${plugin}`);
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function filterOrganizationCapabilities(
  items: readonly OrganizationCapabilityBrowserItemV1[],
  filters: OrganizationCapabilityFilters,
): OrganizationCapabilityBrowserItemV1[] {
  const q = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    if (filters.assetKind !== "all" && item.assetKind !== filters.assetKind) return false;
    if (filters.audience !== "all" && item.sourceAudience.kind !== filters.audience) return false;
    if (filters.health !== "all" && item.sourceReadiness.status !== filters.health) return false;
    if (filters.source !== "all") {
      if (filters.source.startsWith("plugin:")) {
        if (!item.source.pluginNames.includes(filters.source.slice(7))) return false;
      } else if (item.source.kind !== filters.source) return false;
    }
    if (!q) return true;
    const audience = item.sourceAudience.kind === "roles"
      ? item.sourceAudience.roleTemplateIds.join(" ")
      : item.sourceAudience.kind;
    return [item.name, item.description, item.source.kind, ...item.source.pluginNames, audience]
      .join(" ").toLowerCase().includes(q);
  });
}
